import { useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info-icon.svg';

const TOOLTIP_MAX_WIDTH = 340;
const TOOLTIP_APPROX_HEIGHT = 120;
const EDGE_MARGIN = 32;
const GAP = 6;

const tooltipStyle = {
  background: 'rgba(255, 255, 255, 1)',
  color: 'rgba(1, 1, 1, 1)',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.6,
  padding: '12px 16px',
  borderRadius: 16,
  border: '1px solid rgba(231, 231, 231, 1)',
  width: TOOLTIP_MAX_WIDTH,
  boxSizing: 'border-box',
  whiteSpace: 'normal',
  textAlign: 'left',
  pointerEvents: 'none',
  boxShadow: '0px 8px 32px 0px rgba(0, 0, 0, 0.08)',
  zIndex: 19999,
  position: 'fixed',
};

export default function InfoTooltip({ children, iconProps, approxHeight = TOOLTIP_APPROX_HEIGHT, style }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);

  const handleEnter = () => {
    const rect = ref.current.getBoundingClientRect();
    const iconCenterX = rect.left + rect.width / 2;
    const vw = window.innerWidth;

    // Horizontal: center on icon by default, align to icon edge only if centering overflows
    const centered = iconCenterX - TOOLTIP_MAX_WIDTH / 2;
    let left;
    if (centered + TOOLTIP_MAX_WIDTH > vw - EDGE_MARGIN) {
      // Would overflow right edge — right-align tooltip to icon's right edge
      left = rect.right - TOOLTIP_MAX_WIDTH;
    } else if (centered < EDGE_MARGIN) {
      // Would overflow left edge — left-align tooltip to icon's left edge
      left = rect.left;
    } else {
      left = centered;
    }
    // Final safety clamp
    left = Math.max(EDGE_MARGIN, Math.min(left, vw - TOOLTIP_MAX_WIDTH - EDGE_MARGIN));

    // Vertical: below by default, flip above if too close to bottom
    const openUpward = window.innerHeight - rect.bottom < approxHeight + GAP;

    setPos({
      left,
      ...(openUpward
        ? { bottom: window.innerHeight - rect.top + GAP }
        : { top: rect.bottom + GAP }
      ),
    });
  };

  const handleLeave = () => setPos(null);

  return (
    <span
      ref={ref}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      style={{ position: 'relative', height: 16, display: 'inline-flex', alignItems: "center" }}
    >
      <InfoIcon {...iconProps} />
      {pos && ReactDOM.createPortal(
        <div style={{ ...tooltipStyle, left: pos.left, top: pos.top, bottom: pos.bottom }}>
          {children}
        </div>,
        document.body
      )}
    </span>
  );
}
